import { Button } from "@/components/ui/button"
import { ShoppingCart, Plus } from "lucide-react"
import { ProductResponseDto } from "@/api/api-client"
import { Link } from "react-router-dom"
import { apiClient } from "@/api/client"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/hooks/useLanguage"
import { motion } from "framer-motion"
import {
  formatPricePerWeightUnit,
  formatPerUnitWeight,
  getPricePerWeightUnit,
} from "@/lib/productDisplay"

interface ProductCardItemProps {
  product: ProductResponseDto
  onAddToCart: (product: ProductResponseDto) => void
  onContact: (productName: string) => void
  text: {
    price: string
    contact: string
    addToCart: string
    incVat?: string
  }
  viewMode?: "grid" | "list"
}

export const ProductCardItem = ({ product, onAddToCart, onContact, viewMode = "grid" }: ProductCardItemProps) => {
  const { t, language } = useLanguage()
  const text = t.productCatalog
  const imageUrl = apiClient.ensureAbsUrl(product.primaryImageUrl)
  const perWeightUnitPrice = getPricePerWeightUnit(product)
  const perWeightUnitLabel = formatPricePerWeightUnit(
    perWeightUnitPrice,
    product.weightUnit,
    { withTax: !!product.isTaxable, isTaxable: !!product.isTaxable }
  )
  const perUnitSubtext = formatPerUnitWeight(product.weight, product.weightUnit, t.units.perUnit)

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 24 } },
      }}
      key={product.id}
      className={cn(
        "group relative flex h-full bg-background transition-all duration-500 hover:shadow-2xl dark:bg-card/40 hover:border-accent/30 border-[1px] border-border",
        viewMode === "grid" ? "flex-col" : "flex-row items-center gap-6"
      )}
    >
      {/* Corner Markers (Technical Blueprint Detail) - Visible only in Grid view or as an overall card accent */}
      <div className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-accent/20 transition-colors group-hover:border-accent z-10" />
      <div className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-accent/20 transition-colors group-hover:border-accent z-10" />

      <div
        className={cn(
          "relative overflow-hidden bg-muted/30 group/image",
          viewMode === "grid" ? "aspect-[4/5] w-full" : "h-32 w-32 shrink-0 md:h-48 md:w-48"
        )}
      >
        {imageUrl ? (
          <Link to={`/catalog/product/${product.id}`} className="block h-full w-full">
            <img
              src={imageUrl}
              alt={product.name}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover grayscale-[0.2] transition-transform duration-700 ease-out-expo group-hover:scale-110 group-hover:grayscale-0"
            />
          </Link>
        ) : (
          <div className="flex h-full w-full items-center justify-center opacity-10">
            <ShoppingCart className="h-20 w-20" />
          </div>
        )}

        {/* Quick Add Overlay */}
        <Button
          variant="action"
          onClick={(e) => {
            e.preventDefault()
            onAddToCart(product)
          }}
          className="absolute bottom-0 left-0 right-0 flex h-auto w-full translate-y-0 items-center justify-center rounded-none py-6 shadow-2xl transition-all duration-500 ease-out-expo md:translate-y-full md:group-hover:translate-y-0 z-20"
        >
          <span className="font-display text-lg uppercase tracking-widest">{text.addToCart}</span>
        </Button>

        {product.providerName && (
          <div className="absolute left-4 top-4 bg-primary/90 px-3 py-1.5 shadow-sm">
            <span className="text-xs font-bold uppercase leading-none tracking-widest text-primary-foreground">
              {product.providerName}
            </span>
          </div>
        )}
      </div>

      <div className={cn(
        "flex flex-1 flex-col p-6",
        viewMode === "list" ? "border-none py-0 pr-6 pl-0 justify-center" : "border border-t-0 border-border/50"
      )}>
        <Link to={`/catalog/product/${product.id}`} className="mb-2">
          <h3 className="industrial-font text-2xl uppercase leading-none tracking-tight transition-colors group-hover:text-accent">
            {product.name}
          </h3>
        </Link>

        <p className="mb-6 line-clamp-2 text-sm italic leading-relaxed text-muted-foreground">
          {product.description}
        </p>

        <div className="mt-auto flex items-center justify-between">
          <div className="flex flex-col">
            <span className="industrial-font text-2xl leading-none">
              {perWeightUnitLabel}
            </span>
            {perUnitSubtext && (
              <span className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
                {t.units.soldIn} {perUnitSubtext.replace(`/${t.units.perUnit}`, "")}
              </span>
            )}
          </div>

          <Button
            variant="action"
            onClick={(e) => {
              e.preventDefault()
              onAddToCart(product)
            }}
            className="flex h-auto items-center gap-2 rounded-none px-6 py-2 shadow-sm"
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="text-sm uppercase">{text.add}</span>
          </Button>
        </div>
      </div>
    </motion.div>
  )
}

